import { OptionsApi } from '@core/swagger-client';
import { PollWithPoints, WebinarStatus } from '@core/types';
import {
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    ErrorMessage,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    Plus
} from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddPollButton } from '../../AddPollButton';
import { Polls } from '../../Polls';
import { EditPoll } from '../../organism';

export const ManagePoll = () => {
    const { webinarId } = useParams({ strict: false });

    const { data: webinar, error: webinarError, isLoading: webinarLoading } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));
    const { data: webinarStatus } = useQuery(OptionsApi.webinar.getStatus(webinarId!));
    const [polls, setPolls] = useState<PollWithPoints[]>([]);
    const [updateOnly, setUpdateOnly] = useState<boolean>(false);

    useEffect(() => {
        setUpdateOnly(webinarStatus !== WebinarStatus.Planned && webinarStatus !== WebinarStatus.Started);
    }, [webinarStatus]);

    const { data: pollsData, error: pollsError, isLoading: pollsLoading } = useQuery(OptionsApi.poll.get(webinarId!));

    useEffect(() => {
        if (pollsData) {
            setPolls(pollsData);
        }
    }, [pollsData]);

    const addPollMutation = useMutation(OptionsApi.poll.add());
    const addPoll = () => {
        const emptyPoll = { applyWeighting: false, publicResults: true, showPercentagesOnly: false, pollId: crypto.randomUUID(), options: [], question: '' };
        addPollMutation.mutate(
            {
                webinarId: webinarId!,
                ...emptyPoll
            },
            {
                onSuccess: () => {
                    setPolls([...polls, emptyPoll]);
                }
            }
        );
    };

    const deletePollMutation = useMutation(OptionsApi.poll.remove());
    const deletePoll = (poll: PollWithPoints) => {
        deletePollMutation.mutate(
            {
                webinarId: webinarId!,
                ...poll
            },
            {
                onSuccess: () => {
                    setPolls(polls.filter((p) => p.pollId !== poll.pollId));
                }
            }
        );
    };

    const updatePoll = (poll: PollWithPoints, pollIndex: number) => {
        const updatedPolls = [...polls];
        updatedPolls[pollIndex] = poll;
        setPolls(updatedPolls);
    };

    const { t } = useTranslation();

    const error = webinarError ?? pollsError;
    if (error) {
        return <ErrorMessage apiError={error} />;
    }

    if (webinarLoading || pollsLoading || !webinar || !pollsData) {
        return <LoaderPage />;
    }

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>{webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}</PageHeaderContent>
            </PageHeader>
            <ContainerWebinarInner>
                <BackToOriginLink fallback="content" />
                <ContentHeader testId="pollList" contentHeader={t('Content.Poll.PageHeading')} />
                <Polls>
                    {polls.map((poll, pollIndex) => (
                        <EditPoll updateOnly={updateOnly} poll={poll} pollIndex={pollIndex} key={pollIndex} onUpdate={updatePoll} onDelete={deletePoll} />
                    ))}
                    {!updateOnly && (
                        <div className="my-4 ml-10">
                            <AddPollButton onClick={addPoll}>
                                <Plus classNames="size-5 mr-2 stroke-current" />
                                {t('Content.Poll.AddPoll')}
                            </AddPollButton>
                        </div>
                    )}
                    {updateOnly && <div className="my-4 ml-10">{t('Content.Poll.UpdateOnlyWarning')}</div>}
                </Polls>
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
